import React from "react";
import {
  Heading,
  Flex,
  Tag,
  TagLabel,
  Link,
  Stack,
  IconButton,
  Button,
} from "@chakra-ui/react";
import { Merchant } from "../../fetchers/model";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Table as AITable } from "../../utils/Table";
import { FiEdit2, FiPlus } from "react-icons/fi";
import ConfirmationDialog from "../../utils/Confirmation";
import { RiDeleteBin5Line } from "react-icons/ri";
import axios from "axios";
import { Select } from "chakra-react-select";
import { FilterValue, SortBy } from "../../utils/CommonInterface";
import API from "../../config/api";

export default function ListMerchant() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [data, setData] = React.useState<Merchant[]>([]);
  const [pageCount, setPageCount] = React.useState(0);
  const fetchIdRef = React.useRef(0);

  const fetchData = React.useCallback(
    async ({
      pageSize,
      pageIndex,
      globalFilter,
      filters,
      sortBy,
    }: {
      pageSize: number;
      pageIndex: number;
      globalFilter: string;
      filters: FilterValue[];
      sortBy: SortBy[];
    }) => {
      const fetchId = ++fetchIdRef.current;

      setLoading(true);

      const statusFilters = filters.filter(
        (x) => x.id === "status" && x.value.length > 0
      );
      let statusParams =
        (statusFilters.length > 0 &&
          statusFilters[0].value.map((x) => `&status[]=${x}`).join("")) ||
        "";

      let sortByParam =
        (sortBy.length > 0 &&
          `&order=${sortBy[0].desc ? "-" : ""}${sortBy[0].id}`) ||
        "";
      const { data, headers } = await axios.get<Merchant[]>(
        `${API.MERCHANT}?page=${
          pageIndex + 1
        }&per_page=${pageSize}&excepts="merchant_methods"${
          globalFilter ? `&keyword=${globalFilter}` : ""
        }${statusParams}${sortByParam}`
      );
      if (fetchId === fetchIdRef.current) {
        setData(data);
        setPageCount(Math.ceil(parseInt(headers["total"]) / pageSize));

        setLoading(false);
      }
    },
    []
  );

  const statusOptions = [
    "waiting_review",
    "approved",
    "rejected",
    "in_active",
  ].map(function (val) {
    return {
      label: t(`merchant.status.${val}`),
      value: val,
    };
  });

  const tableColumns = [
    {
      Header: t("merchant.company_name"),
      accessor: "company_name" as const,
      disableFilters: true,
    },
    {
      Header: t("merchant.email"),
      accessor: "email" as const,
      disableFilters: true,
    },
    {
      Header: t("merchant.address.country"),
      accessor: "country" as const,
      disableFilters: true,
    },
    {
      Header: t("merchant.status.title"),
      accessor: "status" as const,
      FilterHeader: t("merchant.status.title"),
      Filter: ({ column }: { column: any }) => (
        <Select
          size="sm"
          isMulti
          name={column.id}
          closeMenuOnSelect={false}
          value={statusOptions.filter(
            (x) => column.filterValue && column.filterValue.includes(x.value)
          )}
          options={statusOptions}
          onChange={(option) => column.setFilter(option.map((x) => x.value))}
        />
      ),
      filterable: true,
    },
    {
      Header: "",
      accessor: "action" as const,
      disableFilters: true,
      disableSortBy: true,
    },
  ];

  const tableData = data.map((item) => ({
    company_name: (
      <Link
        color="blue.500"
        href={`/console/merchant/${item.id}`}
        fontWeight={"bold"}
      >
        {`${item.company_name}`}
      </Link>
    ),
    email: item.email,
    country: item.country,
    status: (
      <Tag
        size={"sm"}
        borderRadius="full"
        variant="solid"
        colorScheme={
          item.status === "waiting_review"
            ? "yellow"
            : item.status === "approved"
            ? "blue"
            : item.status === "rejected"
            ? "red"
            : "gray"
        }
      >
        <TagLabel>{t(`merchant.status.${item.status}`)}</TagLabel>
      </Tag>
    ),
    action: (
      <Stack direction={"row"} spacing={1} alignItems={"center"}>
        <IconButton
          icon={<FiEdit2 />}
          aria-label={t("action.edit")}
          variant="link"
          onClick={() => navigate(`/console/merchant/${item.id}/edit`)}
        />
        <ConfirmationDialog
          title={t("merchant.delete.title")}
          description={t("merchant.delete.description", {
            merchant_name: item.company_name,
          })}
          onConfirm={() => deleteItem(item.id)}
        >
          <IconButton
            icon={<RiDeleteBin5Line />}
            aria-label={t("action.delete")}
            variant="link"
          />
        </ConfirmationDialog>
      </Stack>
    ),
  }));

  const deleteItem = async (itemId: string) => {
    await axios.delete<void>(`${API.MERCHANT}/${itemId}`);
    setData(data.filter((x) => x.id !== itemId));
  };

  return (
    <>
      <Flex justifyContent="space-between">
        <Heading as="h4" size="lg" mb="4">
          {t("merchant.title.name")}
        </Heading>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          variant="solid"
          onClick={() => navigate("/console/merchant/new")}
        >
          {t("action.new")}
        </Button>
      </Flex>
      <AITable
        columns={tableColumns}
        data={tableData}
        fetchData={fetchData}
        loading={loading}
        pageCount={pageCount}
        useSearch={true}
        disableFilters={false}
        disableSortBy={false}
      />
    </>
  );
}
